import type { ActionReturn } from 'svelte/action';

interface ObserverAttributes {
  'on:enterview'?: (e: CustomEvent<void>) => void;
  'on:exitview'?: (e: CustomEvent<void>) => void;
}

interface ResizeAttributes {
  'on:resized'?: (
    e: CustomEvent<{
      rect: DOMRectReadOnly;
      border: { blockSize: number; inlineSize: number };
      content: { blockSize: number; inlineSize: number };
    }>
  ) => void;
}

/**
 * Hashes the options to create a unique identifier for the observer given the options
 * this is used to prevent creating multiple observers with the same options
 * @param {object} options the options to hash
 * @returns {string} the hash of the options
 */
function observerHash(options) {
  const jsonString = JSON.stringify(options);
  let hash = 0;
  for (let i = 0; i < jsonString.length; i++) {
    const char = jsonString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
  }
  return hash.toString();
}

let observer = {};
let resizeObserver;

/**
 * @description Intersection Observer directive to observe when an element is in view
 * @param node the element to observe
 * @param options the options to pass to the observer
 * @returns an action to destroy the observer
 * @example
 *  <div
 *    use:observe
 *    on:enterview={({ detail }) => { console.log(detail) }}
 *    on:exitview={({ detail }) => { console.log(detail) }}
 *  />
 */
export function observe(node, options = { threshold: 0 }): ActionReturn<{}, ObserverAttributes> {
  const hash = observerHash(options);
  if (!observer[hash]) {
    observer[hash] = new IntersectionObserver(entries => {
      for (const detail of entries) {
        const { isIntersecting, target } = detail;
        target.dispatchEvent(new CustomEvent(isIntersecting ? 'enterview' : 'exitview', { detail }));
      }
    }, options);
  }
  observer[hash].observe(node);
  return {
    destroy() {
      observer[hash].unobserve(node);
    }
  };
}

/**
 * @description Resize Observer directive to observe when an element is resized
 * @param node the element to observe
 * @returns an action to destroy the observer
 * @example
 *  <div
 *    use:resize
 *    on:resized={({ detail }) => { console.log(detail.rect) }}
 * />
 */
export function resize(node): ActionReturn<{}, ResizeAttributes> {
  if (!resizeObserver) {
    resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        entry.target.dispatchEvent(
          new CustomEvent('resized', {
            detail: {
              rect: entry.contentRect,
              border: entry.borderBoxSize[0],
              content: entry.contentBoxSize[0]
            }
          })
        );
      }
    });
  }
  resizeObserver.observe(node);
  return {
    destroy() {
      resizeObserver.unobserve(node);
    }
  };
}
